import { MproFeeVault } from 'config/abi/types/MproFeeVault'
import { Web3Provider } from '@ethersproject/providers'
import { useCallback, useMemo } from 'react'
import { ethers } from 'ethers'
import { getProviderOrSigner } from 'utils'
import {
  useClassicStakeContractAddress,
  useFeeVaultContractAddress,
  useClassicDoubleStakeContractAddress,
  useMgaLightNodesContractDetails,
  useMgaRegularNodesContractDetails,
  useRegularNodesContractDetails,
  useMproTokenContractAddress,
  useNodeOwnersContractsDetails,
  useRewardProxyContractAddress,
  useStakeContractAddress,
  useUniswapRouterAddress
} from 'state/global/hooks'
import {
  getBep20Contract,
  getMproContract as getMpro,
  getMproStakeRewardContract,
  getUniswapRouterContract,
  getClassicStakeContract,
  getFeeVaultContract,
  getMproRewardProxyContract,
  getUniswapPairContract,
  getMgaRegularNodesContract,
  getMgaLightNodesContract,
  getDoubleAutoStakeContract,
  getClassicDoubleStakeContract,
  getNodeOwnersContract
} from 'utils/contractHelpers'
import { MprolabContractName } from '@mprolab/sdk'
import {
  MproDoubleRewardAutoStake,
  MgaNodes,
  MproRewardProxy,
  MproRewardStake,
  MproStake,
  MproDoubleStake,
  NodeOwners
} from 'config/abi/types'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import useActiveWeb3React from './useActiveWeb3React'
import { ContractType, useWeb3ProviderAndContract } from './useWeb3ProviderAndContract'

export const useBep20Contract = (tokenAddress: string) => {
  const { provider, account } = useActiveWeb3React()
  return useMemo(
    () => getBep20Contract(tokenAddress, getProviderOrSigner(provider as Web3Provider, account)),
    [provider, account, tokenAddress]
  )
}

export const useMproContract = () => {
  const { account, provider, chainId } = useActiveWeb3React()
  const mproContractAddresses = useMproTokenContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()

  const getMproContract = useCallback(
    (targetChainId?: number) => {
      const resolvedChainId = targetChainId ?? chainId
      const mproContractAddress = mproContractAddresses
        .find((contract) => contract.chainId === resolvedChainId && contract.isActive)
        ?.address?.trim()

      if (!mproContractAddress || !ethers.utils.isAddress(mproContractAddress)) {
        throw new Error(`Missing MPRO contract configuration for chain ${resolvedChainId}`)
      }

      return getMpro(mproContractAddress, getProviderOrSignerByChainId(resolvedChainId, account))
    },
    [account, chainId, getProviderOrSignerByChainId, mproContractAddresses, provider]
  )

  return getMproContract
}

export const useNodeOwnersContracts = (): {
  nodeOwnersContracts: NodeOwners[]
} => {
  const { account, provider } = useActiveWeb3React()
  const { getContractByChainId } = useWeb3ProviderAndContract()
  const contracts = useNodeOwnersContractsDetails()

  const nodeOwnersContracts = useMemo(
    (): NodeOwners[] =>
      contracts.map((contract) => {
        return getContractByChainId({
          contractAddress: contract.address,
          chainId: contract.chainId,
          contractType: ContractType.NODE_OWNERS,
          account
        }) as NodeOwners
      }),
    [contracts.length, account, provider]
  )

  return { nodeOwnersContracts }
}

export const useStakeContract = (address: string): MproRewardStake | MproDoubleRewardAutoStake => {
  const stakeContractAddresses = useStakeContractAddress()
  const mproStakeRewardAddress = stakeContractAddresses?.find((contract) => contract.address === address)
  const isDual = mproStakeRewardAddress?.name === MprolabContractName.MPRO_DUAL_AUTO_STAKE

  return useMemo(
    () =>
      isDual
        ? getDoubleAutoStakeContract(
            mproStakeRewardAddress?.address,
            getSimpleRpcProviderByChainId(mproStakeRewardAddress?.chainId)
          )
        : getMproStakeRewardContract(
            mproStakeRewardAddress?.address,
            getSimpleRpcProviderByChainId(mproStakeRewardAddress?.chainId)
          ),
    [address, isDual, mproStakeRewardAddress?.address, mproStakeRewardAddress?.chainId]
  )
}

export const useStakeWriteContract = (address: string): MproRewardStake | MproDoubleRewardAutoStake => {
  const { provider, account } = useActiveWeb3React()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const stakeContractAddresses = useStakeContractAddress()
  const mproStakeRewardAddress = stakeContractAddresses?.find((contract) => contract.address === address)
  const isDual = mproStakeRewardAddress?.name === MprolabContractName.MPRO_DUAL_AUTO_STAKE

  return useMemo(
    () =>
      isDual
        ? getDoubleAutoStakeContract(
            mproStakeRewardAddress?.address,
            getProviderOrSignerByChainId(mproStakeRewardAddress?.chainId, account)
          )
        : getMproStakeRewardContract(
            mproStakeRewardAddress?.address,
            getProviderOrSignerByChainId(mproStakeRewardAddress?.chainId, account)
          ),
    [account, provider, address, isDual, mproStakeRewardAddress?.address, mproStakeRewardAddress?.chainId]
  )
}

export const useUniswapRouterContract = () => {
  const { provider, account } = useActiveWeb3React()
  const uniswapRouterContractAddresses = useUniswapRouterAddress()
  const uniswapRouterAddress = uniswapRouterContractAddresses?.find((contract) => contract.isActive)

  return useMemo(
    () =>
      getUniswapRouterContract(
        uniswapRouterAddress?.address,
        getSimpleRpcProviderByChainId(uniswapRouterAddress?.chainId)
      ),
    [provider, account]
  )
}

/** The fee vault(s), read-only, one per registered collection. */
export const useFeeVaultContract = (): MproFeeVault[] => {
  const addresses = useFeeVaultContractAddress()
  return useMemo(
    (): MproFeeVault[] =>
      addresses.map((contract) => getFeeVaultContract(contract?.address, getSimpleRpcProviderByChainId(contract?.chainId))),
    [addresses]
  )
}

/** The fee vault(s) bound to the connected wallet's signer, for claim(). */
export const useFeeVaultWriteContract = (): MproFeeVault[] => {
  const { provider, account } = useActiveWeb3React()
  const addresses = useFeeVaultContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  return useMemo(
    (): MproFeeVault[] =>
      addresses.map((contract) => getFeeVaultContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))),
    [addresses.length, account, provider]
  )
}

export const useClassicStakeContract = (): MproStake[] => {
  const classicStakeContractAddresses = useClassicStakeContractAddress()
  return useMemo(
    (): MproStake[] =>
      classicStakeContractAddresses.map((contract) => {
        return getClassicStakeContract(contract?.address, getSimpleRpcProviderByChainId(contract?.chainId))
      }),
    [classicStakeContractAddresses]
  )
}

export const useClassicDoubleStakeContract = (): MproDoubleStake[] => {
  const classicStakeContractAddresses = useClassicDoubleStakeContractAddress()

  return useMemo(
    (): MproDoubleStake[] =>
      classicStakeContractAddresses.map((contract) => {
        return getClassicDoubleStakeContract(contract?.address, getSimpleRpcProviderByChainId(contract?.chainId))
      }),
    [classicStakeContractAddresses]
  )
}

export const useClassicStakeWriteContract = (): MproStake[] => {
  const { provider, account } = useActiveWeb3React()
  const classicStakeContractAddresses = useClassicStakeContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()

  return useMemo(
    (): MproStake[] =>
      classicStakeContractAddresses.map((contract) => {
        return getClassicStakeContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))
      }),
    [classicStakeContractAddresses.length, account, provider]
  )
}

export const useClassicDoubleStakeWriteContract = (): MproDoubleStake[] => {
  const { provider, account } = useActiveWeb3React()
  const classicStakeContractAddresses = useClassicDoubleStakeContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()

  return useMemo(
    (): MproDoubleStake[] =>
      classicStakeContractAddresses.map((contract) => {
        return getClassicDoubleStakeContract(
          contract?.address,
          getProviderOrSignerByChainId(contract?.chainId, account)
        )
      }),
    [classicStakeContractAddresses.length, account, provider]
  )
}

export const useRewardProxyContract = (): MproRewardProxy[] => {
  const { provider, account } = useActiveWeb3React()
  const rewardProxyContractAddresses = useRewardProxyContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  return useMemo(
    (): MproRewardProxy[] =>
      rewardProxyContractAddresses.map((contract) => {
        return getMproRewardProxyContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))
      }),
    [rewardProxyContractAddresses.length, account, provider]
  )
}

export const useUniswapPairContract = (address: string, chainId: number) => {
  const { provider, account } = useActiveWeb3React()

  return useMemo(() => getUniswapPairContract(address, getSimpleRpcProviderByChainId(chainId)), [provider, account])
}

export const useMgaRegularNodesContracts = (): MgaNodes[] => {
  const { provider, account } = useActiveWeb3React()
  const mgaRegularNodesContractAddresses = useMgaRegularNodesContractDetails()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  return useMemo(
    (): MgaNodes[] =>
      mgaRegularNodesContractAddresses.map((contract) =>
        getMgaRegularNodesContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))
      ),
    [mgaRegularNodesContractAddresses.length, account, provider]
  )
}

export const useMgaLightNodesContracts = (): MgaNodes[] => {
  const { provider, account } = useActiveWeb3React()
  const mgaLightNodesContractAddresses = useMgaLightNodesContractDetails()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  return useMemo(
    (): MgaNodes[] =>
      mgaLightNodesContractAddresses.map((contract) =>
        getMgaLightNodesContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))
      ),
    [mgaLightNodesContractAddresses.length, account, provider]
  )
}

export const useRegularNodesContracts = (): MgaNodes[] => {
  const { provider, account } = useActiveWeb3React()
  const regularNodesContractAddresses = useRegularNodesContractDetails()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  return useMemo(
    (): MgaNodes[] =>
      regularNodesContractAddresses.map((contract) =>
        getMgaRegularNodesContract(contract?.address, getProviderOrSignerByChainId(contract?.chainId, account))
      ),
    [regularNodesContractAddresses.length, account, provider]
  )
}
